import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { type Task, task } from 'ember-concurrency';
import type MarketSettingsPropertyTagsController from 'lumen/controllers/market/settings/property-tags';
import type { PropertyTags } from 'lumen/models';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import protectedRoute from 'lumen/utils/decorators/protected-route';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import {
  setupUnsavedChangesTransitionHandler,
  type TransitionHandler,
} from 'lumen/utils/unsaved-changes-transition-handlers';

interface PropertyTagsRouteModel {
  propertyTags: Task<PropertyTags[], []>;
}

// @ts-expect-error: Unable to resolve signature of class decorator when called as an expression.
@protectedRoute({
  features: ['settings.property_tags'],
  redirectTo: 'market.settings',
})
export default class MarketSettingsPropertyTagsRoute extends Route {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  #transitionHandler?: TransitionHandler;

  requestPropertyTagsTask = task({ restartable: true }, async () => {
    const marketId = this.hotelAndMarketService.market.id;

    try {
      const propertyTags = await this.store.query<PropertyTags>(
        'property-tags',
        {
          namespace: APIGATEWAY,
          market_id: marketId,
        },
      );

      return [...propertyTags];
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model() {
    const model: PropertyTagsRouteModel = {
      propertyTags: this.requestPropertyTagsTask,
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    model.propertyTags.perform();

    return model;
  }

  resetController(
    controller: MarketSettingsPropertyTagsController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (isExiting && this.#transitionHandler) {
      this.#transitionHandler.cleanup();
      this.#transitionHandler = undefined;
    }
  }

  setupController(
    controller: MarketSettingsPropertyTagsController,
    model: unknown,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    this.#transitionHandler = setupUnsavedChangesTransitionHandler(
      this.router,
      controller,
      'market.settings.property-tags',
    );
  }
}
